<i18n lang="yaml">
en:
  next: Next
  prev: Prev

fr:
  next: Suivant
  prev: Précédent
</i18n>

<template>
  <div class="pagination my-12">
    <CommonInputButton :disabled="currentPage <= 1" variant="primary-350" @click="prev()">{{
      t('prev')
    }}</CommonInputButton>
    <CommonInputButton
      v-for="index in pages"
      :key="index"
      :variant="currentPage === index ? 'primary' : 'primary-350'"
      @click="goToPage(index)"
    >
      {{ index }}
    </CommonInputButton>
    <CommonInputButton :disabled="currentPage >= totalPages" variant="primary-350" @click="next()">{{
      t('next')
    }}</CommonInputButton>
  </div>
</template>

<script lang="ts" setup>
import { AppProviderKeys } from '~/providers'
import type { IPPagination } from '~/types/provider/pagination'

const props = defineProps<{
  pageSize: number
  totalResults: number
}>()

const { t } = useI18n()

const pagination = inject<IPPagination>(AppProviderKeys.Pagination) as IPPagination

const currentPage = computed(() => pagination.value.currentPage)

const totalPages: ComputedRef<number> = computed((): number => {
  return Math.ceil(props.totalResults / props.pageSize)
})

const pages: ComputedRef<number[]> = computed((): number[] => {
  let start = 1
  if (currentPage.value > 3) {
    if (totalPages.value - currentPage.value + 1 < 5) {
      start = Math.max(totalPages.value - 5, 1)
    } else {
      start = Math.max(currentPage.value - 2, 1)
    }
  }
  return Array.apply(null, new Array(Math.min(totalPages.value, 5))).map((_, i) => i + start)
})

function prev() {
  goToPage(currentPage.value - 1)
}

function next() {
  goToPage(currentPage.value + 1)
}

function goToPage(page: number) {
  if (page <= 1) pagination.updateCurrentPage(1)
  else if (page > totalPages.value) pagination.updateCurrentPage(totalPages.value)
  else pagination.updateCurrentPage(page)

  document.getElementsByTagName('h1')[0].scrollIntoView({ behavior: 'smooth' })
}
</script>

<style lang="scss" scoped>
.pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  grid-column-gap: 0.25rem;
}

a,
button {
  border: 1px solid $primary-500;
  border-radius: 2rem;
  padding: 0.5rem 0.9rem;
  font-size: 1rem;
  cursor: pointer;

  &:hover {
    background-color: $primary-350;
  }

  &.active {
    color: #fff;
    background-color: $highlight-500;
    border-color: $highlight-500;
  }

  &:disabled {
    color: #6c757d;
    pointer-events: none;
    cursor: auto;
  }
}
</style>
